import { AbstractEffect } from './abstract.effect'
import { evaluateSmartEnvelope } from '@/editor/smart/smartEnvelope'

const phaseSecFrom = (twc, mod, settings) => {
  let phaseBeats = 0
  if (Number.isFinite(Number(mod && mod.phaseBeats))) phaseBeats = Number(mod.phaseBeats)
  if (Number.isFinite(Number(twc && twc.phaseBeats))) phaseBeats = Number(twc.phaseBeats)

  let bc = null
  try {
    const { makeBeatCode } = require('@/etc/beatcode')
    bc = makeBeatCode(settings || {})
  } catch (e) {
    bc = null
  }

  const quarterSec = bc
    ? bc.secondsPerQuarter()
    : (60 / ((settings && settings.bpm) || 120))

  return phaseBeats * quarterSec
}

const clamp = (v, min, max) => {
  if (v <= min) return min
  if (v >= max) return max
  return v
}

const pFromAbs = (schema, v) => {
  const min = Number.isFinite(Number(schema && schema.min)) ? Number(schema.min) : 0
  const max = Number.isFinite(Number(schema && schema.max)) ? Number(schema.max) : 1
  const vv = Number.isFinite(Number(v)) ? Number(v) : 0
  if (min < 0) {
    if (vv >= 0) return max ? (vv / max) * 100 : 0
    return min ? (vv / (-min)) * 100 : 0
  }
  return max ? (vv / max) * 100 : 0
}

const absFromP = (schema, p) => {
  const min = Number.isFinite(Number(schema && schema.min)) ? Number(schema.min) : 0
  const max = Number.isFinite(Number(schema && schema.max)) ? Number(schema.max) : 1
  const pp = Number.isFinite(Number(p)) ? Number(p) : 0
  if (min < 0) {
    if (pp >= 0) return (pp / 100) * max
    return (pp / 100) * (-min)
  }
  return (pp / 100) * max
}

const snapToStep = (schema, v) => {
  const min = Number.isFinite(Number(schema && schema.min)) ? Number(schema.min) : 0
  const max = Number.isFinite(Number(schema && schema.max)) ? Number(schema.max) : 1
  const step = Number.isFinite(Number(schema && schema.step)) ? Number(schema.step) : null
  let vv = Number.isFinite(Number(v)) ? Number(v) : 0
  vv = clamp(vv, min, max)
  if (!step || step <= 0) return vv
  const snapped = min + Math.round((vv - min) / step) * step
  return clamp(snapped, min, max)
}

export class BlurEffect extends AbstractEffect {
  static metaInfo() {
    return {
      uuid: 'blur',
      code: 'blur',
      name: 'Blur',
      // Scene is no longer a timeline strip; blur is intended as a global (camera/adjustment) post effect.
      apply: ['camera', 'adjustment'],
      defaults: {
        amount: 0,
        x: 0,
        y: 0
      }
    }
  }

  constructor(suuid, uuid) {
    super(suuid, uuid)
    this._controls = {
      values: { amount: 0, x: 0, y: 0 },
      auto: { amount: 'none', x: 'none', y: 'none' }
    }
  }

  info() {
    const ret = super.info()
    ret.controls = this._controls
    return ret
  }

  configure(data) {
    super.configure(data)
    this._controls = data.controls || this._controls
  }

  frame(time, props, ctx) {
    if (!this._controls.values || !this._controls.auto) return props

    // Smart blur only makes sense for adjustment/camera strips.
    // Editor.updateAnimation passes the real strip + settings in ctx.
    const hostStrip = (ctx && ctx.strip) ? ctx.strip : null
    const settings = (ctx && ctx.settings) ? ctx.settings : {}
    const isAdjustment = !!(
      hostStrip &&
      hostStrip.asset &&
      (hostStrip.asset.type === 'adjustment' || hostStrip.asset.type === 'camera')
    )

    // Cache UI schema for mapping percent<->abs if available (BLUR_EFFECTS.blur.controls)
    if (!this._controls.schema) {
      this._controls.schema = {}
      try {
        const { BLUR_EFFECTS } = require('@/etc/consts')
        const E = BLUR_EFFECTS && BLUR_EFFECTS.blur
        const controls = (E && E.controls) ? E.controls : {}
        Object.keys(controls).forEach((k) => {
          this._controls.schema[k] = controls[k]
        })
      } catch (e) {
        this._controls.schema = {}
      }
    }

    const cfg = Object.keys(this._controls.values).reduce((acc, code) => {
      const binding = this._controls.auto[code]
      const smartGroup = this._controls.smart ? this._controls.smart[code] : null
      const schema = (this._controls.schema && this._controls.schema[code]) ? this._controls.schema[code] : null
      const fallbackSchema = schema || { min: 0, max: 1, step: null }
      const mod = (this._controls.mod && this._controls.mod[code]) ? this._controls.mod[code] : null
      
      // Smart binding (only valid on adjustment/camera)
      if (binding === 'smart' && smartGroup && isAdjustment) {
        const globalTime = (typeof ctx.globalTime === 'number') ? ctx.globalTime : time
        acc[code] = evaluateSmartEnvelope(smartGroup, {
          localTime: time,
          globalTime: globalTime,
          stripStart: Number(hostStrip.start) || 0,
          stripLength: Number(hostStrip.length) || 0,
          settings,
          scope: 'global'
        })
      } 
      // Legacy Tween binding
      else if (binding && binding !== 'none' && binding !== 'smart') {
        const twc = (this._tweens && this._tweens[binding]) ? this._tweens[binding] : null
        const phaseSec = phaseSecFrom(twc, mod, settings)
        acc[code] = this.value(time - phaseSec, binding)
      }
      // Static value
      else {
        acc[code] = this._controls.values[code]
      }

      // Phase 2: Apply dynamic window in percent domain when mod exists (clamp + step snap)
      if (mod && Number.isFinite(Number(mod.rangeLowP)) && Number.isFinite(Number(mod.rangeHighP))) {
        const lowP = Number(mod.rangeLowP)
        const highP = Number(mod.rangeHighP)
        const p = pFromAbs(fallbackSchema, acc[code])
        const p2 = clamp(p, Math.min(lowP, highP), Math.max(lowP, highP))
        acc[code] = snapToStep(fallbackSchema, absFromP(fallbackSchema, p2))
      }
      return acc
    }, {})

    // Add blur pass to next frame
    ctx.editor.addNextPass({
      uuid: this.uuid,
      code: 'blur',
      cfg
    })

    return props
  }
}
